import classNames from "classnames";
import React from "react";

import { Column, DataWithId } from "./types";

const idCellStyles = "tw-mr-1 tw-rounded tw-bg-blue-1 tw-text-blue-4";
const lockedCellStyles = "tw-bg-primary-25 tw-text-primaryDim-300";
const readOnlyCellStyles = "tw-bg-neutral-25 tw-text-neutral-300";
const actionsCellStyles =
  "tw-sticky tw-right-0 tw-flex tw-items-center tw-gap-1 tw-bg-neutral-25 tw-pl-2 tw-pr-0";
const middleCellStyles =
  "tw-border-l-0 tw-border-r tw-border-solid tw-border-neutral-100";
const rowStartCellStyles =
  "tw-rounded-l tw-border-x tw-border-solid tw-border-neutral-100";
const rowEndCellStyles =
  "tw-rounded-r tw-border-l-0 tw-border-r tw-border-solid tw-border-neutral-100";

function TableCellContainer<R extends DataWithId>({
  children,
  isRowEnd,
  isRowStart,
  isTextContent,
  type,
}: {
  children?: React.ReactNode;
  isRowEnd?: boolean;
  isRowStart?: boolean;
  isTextContent?: boolean;
  type: Column<R>["type"];
}) {
  const isIdCell = type === "id";
  const isLockedCell = type === "locked";
  const isReadOnlyCell = type === "readOnly";
  const isActionsCell = type === "actions";
  const isMiddleCell =
    !isRowEnd && !isRowStart && (isLockedCell || isReadOnlyCell);

  return (
    <div
      className={classNames(
        "tw-box-border tw-flex tw-h-10 tw-items-center tw-border-y tw-px-2.5",
        {
          // Added because text will not overflow as ellipsis in responsive mode
          "tw-min-w-0": isTextContent,
          [idCellStyles]: isIdCell,
          [lockedCellStyles]: isLockedCell,
          [readOnlyCellStyles]: isReadOnlyCell,
          [actionsCellStyles]: isActionsCell,
          [middleCellStyles]: isMiddleCell,
          [rowStartCellStyles]: isRowStart,
          [rowEndCellStyles]: isRowEnd,
        },
      )}
    >
      {isTextContent ? (
        <p className="tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-text-sm tw-font-semibold">
          {children}
        </p>
      ) : (
        children
      )}
    </div>
  );
}

export function TableRow<R extends DataWithId>({
  row,
  columns,
}: {
  row: R;
  columns: Column<R>[];
}) {
  return (
    <>
      {columns.map(({ isRowEnd, isRowStart, key, title, type, Component }) => {
        const children = Component
          ? Component(row)
          : (row[key] as React.ReactNode);

        return (
          <TableCellContainer
            key={type === "actions" ? "actions" : title}
            isRowEnd={isRowEnd}
            isRowStart={isRowStart}
            isTextContent={typeof children === "string"}
            type={type}
          >
            {children}
          </TableCellContainer>
        );
      })}
    </>
  );
}
